/**
 * Rosie — NEMT Dispatch Platform
 * Copyright (c) 2025-2026 Commonwealth Transport LLC. All rights reserved.
 * PROPRIETARY AND CONFIDENTIAL. Unauthorized use, copying, or distribution is prohibited.
 */
import React, { useState, useEffect } from 'react';
import api from '../utils/api';

const BLANK_DRIVER = {
  firstName: '', lastName: '', email: '', phone: '',
  hourlyRate: 40, isFullTime: false, dispatchPriority: 2,
  canDoWheelchair: true, canDoSedan: true, canDoBehavioralHealth: false,
  bh201Capable: false, bh302Capable: false, bhCalmOnly: false,
  avoidAggressive302: false, needsStrongPartner: false, isAttendantOnly: false,
  speedRating: 'moderate', targetHoursMin: 15, targetHoursMax: 25,
  specialNotes: '', completionReminder: '', status: 'active', portalPin: '', scheduleType: 'flexible', overnightBuffer: '',
  licenseNumber: '', licenseExpiration: '', licenseState: 'PA',
};

function Toggle({ label, value, onChange, hint }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '8px 0', borderBottom: '0.5px solid #f0f0f0' }}>
      <div>
        <div style={{ fontSize: 13 }}>{label}</div>
        {hint && <div style={{ fontSize: 11, color: '#aaa', marginTop: 1 }}>{hint}</div>}
      </div>
      <div onClick={() => onChange(!value)} style={{
        width: 36, height: 20, borderRadius: 10, cursor: 'pointer',
        background: value ? '#185FA5' : '#ddd',
        position: 'relative', transition: 'background 0.15s', flexShrink: 0
      }}>
        <div style={{
          width: 16, height: 16, borderRadius: '50%', background: 'white',
          position: 'absolute', top: 2,
          left: value ? 18 : 2, transition: 'left 0.15s'
        }} />
      </div>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div style={{ marginBottom: 12 }}>
      <label style={{ fontSize: 12, color: '#666', display: 'block', marginBottom: 4 }}>{label}</label>
      {children}
    </div>
  );
}

export default function EditDriverPanel({ driver, onClose, onSaved }) {
  const isNew = !driver?.id;
  const [form, setForm] = useState(BLANK_DRIVER);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (driver && driver.id) {
      const firstName = driver.firstName || driver.first_name || (driver.name ? driver.name.split(' ')[0] : '') || '';
      const lastName = driver.lastName || driver.last_name || (driver.name ? driver.name.split(' ').slice(1).join(' ') : '') || '';
      setForm({
        firstName,
        lastName,
        email: driver.email || '',
        phone: driver.phone || '',
        hourlyRate: driver.hourlyRate || driver.hourly_rate || 40,
        isFullTime: driver.isFullTime ?? driver.is_full_time ?? false,
        dispatchPriority: driver.dispatchPriority || driver.dispatch_priority || 2,
        canDoWheelchair: driver.capabilities?.wheelchair ?? driver.can_do_wheelchair ?? true,
        canDoSedan: driver.capabilities?.sedan ?? driver.can_do_sedan ?? true,
        canDoBehavioralHealth: driver.capabilities?.bh201 ?? driver.can_do_behavioral_health ?? false,
        bh201Capable: driver.capabilities?.bh201 ?? driver.bh_201_capable ?? false,
        bh302Capable: driver.capabilities?.bh302 ?? driver.bh_302_capable ?? false,
        bhCalmOnly: driver.capabilities?.bhCalmOnly ?? driver.bh_calm_only ?? false,
        avoidAggressive302: driver.capabilities?.avoidAggressive302 ?? driver.avoid_aggressive_302 ?? false,
        needsStrongPartner: driver.capabilities?.needsStrongPartner ?? driver.needs_strong_partner ?? false,
        isAttendantOnly: driver.capabilities?.attendantOnly ?? driver.is_attendant_only ?? false,
        speedRating: driver.speedRating || driver.speed_rating || 'moderate',
        targetHoursMin: parseFloat(driver.target_hours_min ?? driver.weeklyStats?.targetHoursMin ?? 15),
        targetHoursMax: parseFloat(driver.target_hours_max ?? driver.weeklyStats?.targetHoursMax ?? 25),
        specialNotes: driver.special_notes || driver.specialNotes || '',
        completionReminder: driver.completion_reminder || driver.completionReminder || '',
        status: driver.status || 'active',
        portalPin: driver.portal_pin || '',
        scheduleType: driver.schedule_type || 'flexible',
        overnightBuffer: driver.overnight_buffer_minutes || '',
        licenseNumber: driver.license_number || '',
        licenseExpiration: driver.license_expiration ? String(driver.license_expiration).slice(0, 10) : '',
        licenseState: driver.license_state || 'PA',
      });
    }
  }, [driver]);

  const setField = (key, val) => setForm(f => ({ ...f, [key]: val }));

  const handleSave = async () => {
    if (!form.firstName || !form.lastName) {
      setError('First and last name are required');
      return;
    }
    setSaving(true);
    setError('');
    try {
      if (isNew) {
        await api.post('/drivers', {
          firstName: form.firstName,
          lastName: form.lastName,
          email: form.email || null,
          phone: form.phone || null,
          hourlyRate: parseFloat(form.hourlyRate),
          isFullTime: form.isFullTime,
          dispatchPriority: parseInt(form.dispatchPriority),
          canDoWheelchair: form.canDoWheelchair,
          canDoSedan: form.canDoSedan,
          canDoBehavioralHealth: form.canDoBehavioralHealth,
          bh201Capable: form.bh201Capable,
          bh302Capable: form.bh302Capable,
          bhCalmOnly: form.bhCalmOnly,
          avoidAggressive302: form.avoidAggressive302,
          needsStrongPartner: form.needsStrongPartner,
          isAttendantOnly: form.isAttendantOnly,
          speedRating: form.speedRating,
          targetHoursMin: parseFloat(form.targetHoursMin),
          targetHoursMax: parseFloat(form.targetHoursMax),
          specialNotes: form.specialNotes,
        });
      } else {
        await api.put(`/drivers/${driver.id}`, {
          email: form.email || null,
          phone: form.phone || null,
          hourlyRate: parseFloat(form.hourlyRate),
          isFullTime: form.isFullTime,
          dispatchPriority: parseInt(form.dispatchPriority),
          targetHoursMin: parseFloat(form.targetHoursMin),
          targetHoursMax: parseFloat(form.targetHoursMax),
          specialNotes: form.specialNotes,
          completionReminder: form.completionReminder,
          status: form.status,
          canDoWheelchair: form.canDoWheelchair,
          canDoSedan: form.canDoSedan,
          canDoBehavioralHealth: form.canDoBehavioralHealth,
          bh201Capable: form.bh201Capable,
          bh302Capable: form.bh302Capable,
          bhCalmOnly: form.bhCalmOnly,
          avoidAggressive302: form.avoidAggressive302,
          needsStrongPartner: form.needsStrongPartner,
          isAttendantOnly: form.isAttendantOnly,
          speedRating: form.speedRating,
          portalPin: form.portalPin || null,
          scheduleType: form.scheduleType || 'flexible',
          overnightBuffer: form.overnightBuffer ? Number(form.overnightBuffer) : null,
          licenseNumber: form.licenseNumber || null,
          licenseExpiration: form.licenseExpiration || null,
          licenseState: form.licenseState || 'PA',
        });
      }
      onSaved();
      onClose();
    } catch (err) {
      setError('Could not save — ' + (err.response?.data?.error || err.message));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div style={{ position: 'fixed', inset: 0, zIndex: 1000, display: 'flex', justifyContent: 'flex-end' }}>
      <div onClick={onClose} style={{ flex: 1, background: 'rgba(0,0,0,0.3)', zIndex: 1000 }} />
      <div style={{ width: '100%', maxWidth: 420, background: 'white', display: 'flex', flexDirection: 'column', overflowY: 'auto', position: 'relative', zIndex: 1001 }}>
        <div style={{ padding: '16px 20px', borderBottom: '0.5px solid #e0e0db', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <h2 style={{ fontSize: 16, fontWeight: 500, margin: 0 }}>
            {isNew ? 'Add driver' : `Edit — ${form.firstName} ${form.lastName}`}
          </h2>
          <button onClick={onClose} style={{ fontSize: 13, padding: '4px 10px' }}>Cancel</button>
        </div>

        <div style={{ padding: '16px 20px', flex: 1 }}>
          {error && (
            <div style={{ background: '#FCEBEB', color: '#A32D2D', padding: '8px 12px', borderRadius: 8, fontSize: 13, marginBottom: 12 }}>
              {error}
            </div>
          )}

          <div style={{ fontSize: 11, color: '#aaa', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 10 }}>Basic info</div>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
            <Field label="First name">
              <input value={form.firstName} onChange={e => setField('firstName', e.target.value)} style={{ width: '100%' }} />
            </Field>
            <Field label="Last name">
              <input value={form.lastName} onChange={e => setField('lastName', e.target.value)} style={{ width: '100%' }} />
            </Field>
          </div>

          <Field label="Email">
            <input type="email" value={form.email} onChange={e => setField('email', e.target.value)} style={{ width: '100%' }} />
          </Field>
          <Field label="Phone">
            <input type="tel" value={form.phone} onChange={e => setField('phone', e.target.value)} style={{ width: '100%' }} />
          </Field>
          <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr', gap: 10 }}>
            <Field label="License number">
              <input value={form.licenseNumber} onChange={e => setField('licenseNumber', e.target.value)} placeholder="DL number" style={{ width: '100%' }} />
            </Field>
            <Field label="License state">
              <input value={form.licenseState} onChange={e => setField('licenseState', e.target.value)} placeholder="PA" maxLength={2} style={{ width: '100%' }} />
            </Field>
          </div>
          <Field label="License expiration">
            <input type="date" value={form.licenseExpiration} onChange={e => setField('licenseExpiration', e.target.value)} style={{ width: '100%' }} />
          </Field>
          <Field label="Schedule type">
            <select value={form.scheduleType} onChange={e => setField('scheduleType', e.target.value)} style={{ width: '100%' }}>
              <option value="flexible">Flexible — driver sets own hours</option>
              <option value="set">Set schedule — changes require approval</option>
            </select>
          </Field>
          <Field label="Overnight buffer (minutes)">
            <input type="number" value={form.overnightBuffer} onChange={e => setField('overnightBuffer', e.target.value)} placeholder="e.g. 30" style={{ width: '100%' }} />
          </Field>
          <Field label="Portal PIN (4 digits)">
            <input
              type="text"
              value={form.portalPin}
              onChange={e => setField('portalPin', e.target.value.replace(/\D/g, '').slice(0, 4))}
              placeholder="e.g. 1234"
              maxLength={4}
              style={{ width: '100%', letterSpacing: '0.2em', fontSize: 16 }}
            />
          </Field>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
            <Field label="Hourly rate ($)">
              <input type="number" value={form.hourlyRate} onChange={e => setField('hourlyRate', e.target.value)} style={{ width: '100%' }} />
            </Field>
            <Field label="Priority">
              <select value={form.dispatchPriority} onChange={e => setField('dispatchPriority', e.target.value)} style={{ width: '100%' }}>
                <option value={1}>1 — Full-time</option>
                <option value={2}>2 — Part-time</option>
                <option value={3}>3 — Occasional</option>
              </select>
            </Field>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
            <Field label="Min hrs/week">
              <input type="number" value={form.targetHoursMin} onChange={e => setField('targetHoursMin', e.target.value)} style={{ width: '100%' }} />
            </Field>
            <Field label="Max hrs/week">
              <input type="number" value={form.targetHoursMax} onChange={e => setField('targetHoursMax', e.target.value)} style={{ width: '100%' }} />
            </Field>
          </div>

          <Field label="Speed rating">
            <select value={form.speedRating} onChange={e => setField('speedRating', e.target.value)} style={{ width: '100%' }}>
              <option value="fast">Fast</option>
              <option value="moderate">Moderate</option>
              <option value="slow">Slow</option>
            </select>
          </Field>

          {!isNew && (
            <Field label="Status">
              <select value={form.status} onChange={e => setField('status', e.target.value)} style={{ width: '100%' }}>
                <option value="active">Active</option>
                <option value="inactive">Inactive</option>
                <option value="suspended">Suspended</option>
              </select>
            </Field>
          )}

          <div style={{ fontSize: 11, color: '#aaa', textTransform: 'uppercase', letterSpacing: '0.05em', margin: '16px 0 10px' }}>Capabilities</div>

          <Toggle label="Full-time" value={form.isFullTime} onChange={v => setField('isFullTime', v)} />
          <Toggle label="Can drive sedan" value={form.canDoSedan} onChange={v => setField('canDoSedan', v)} />
          <Toggle label="Can drive wheelchair van" value={form.canDoWheelchair} onChange={v => setField('canDoWheelchair', v)} />
          <Toggle label="Attendant only (never drives)" value={form.isAttendantOnly} onChange={v => setField('isAttendantOnly', v)} hint="Rides along for BH support, does not drive" />

          <div style={{ fontSize: 11, color: '#aaa', textTransform: 'uppercase', letterSpacing: '0.05em', margin: '16px 0 10px' }}>BH capabilities</div>

          <Toggle label="201 capable" value={form.bh201Capable} onChange={v => setField('bh201Capable', v)} />
          <Toggle label="302 capable" value={form.bh302Capable} onChange={v => setField('bh302Capable', v)} />
          <Toggle label="Calm patients only" value={form.bhCalmOnly} onChange={v => setField('bhCalmOnly', v)} hint="Only cooperative/calm BH patients" />
          <Toggle label="Avoid aggressive 302" value={form.avoidAggressive302} onChange={v => setField('avoidAggressive302', v)} hint="Do not assign to aggressive/elopement patients" />
          <Toggle label="Needs strong partner" value={form.needsStrongPartner} onChange={v => setField('needsStrongPartner', v)} hint="Requires capable second for difficult patients" />

          <div style={{ fontSize: 11, color: '#aaa', textTransform: 'uppercase', letterSpacing: '0.05em', margin: '16px 0 10px' }}>Notes</div>

          <Field label="Special notes">
            <textarea value={form.specialNotes} onChange={e => setField('specialNotes', e.target.value)} rows={3} style={{ width: '100%', resize: 'vertical' }} />
          </Field>
          <Field label="Pop-up after every trip completion (driver portal)">
            <textarea value={form.completionReminder} onChange={e => setField('completionReminder', e.target.value)} rows={3}
              placeholder="Leave blank for none. Shown full-screen in the driver's portal every time they mark a trip complete; they must tap to dismiss."
              style={{ width: '100%', resize: 'vertical' }} />
          </Field>
        </div>

        <div style={{ padding: '12px 20px', borderTop: '0.5px solid #e0e0db', display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
          <button onClick={onClose} style={{ fontSize: 13 }}>Cancel</button>
          <button onClick={handleSave} disabled={saving} style={{
            fontSize: 13, background: '#185FA5', color: 'white',
            border: '0.5px solid #185FA5', padding: '6px 16px'
          }}>
            {saving ? 'Saving...' : isNew ? 'Add driver' : 'Save changes'}
          </button>
        </div>
      </div>
    </div>
  );
}